/**
 * "Leitores parecidos" — regras PURAS do cartão (blueprint
 * `docs/systems/people-suggestions.md` §5, §7.1, §7.2 e §8). Sem React, sem
 * Firebase: o que decide copy, destino do toque e porta do clube mora aqui,
 * onde um teste prova a regra sem montar tela.
 */
import type { UserProfile } from "../../../services/firebase/userService";
import type { PeopleSuggestion } from "../../../services/firebase/peopleSuggestionsService";

/** Lista publicada só com 3 ou mais (Q3). Abaixo disso a seção some. */
export const PEOPLE_LIST_MIN = 3;

/**
 * Mínimo de LIDOS em comum para o Match dar número — espelho do
 * `MIN_OVERLAP` de `functions/src/social/computeUserMatch.ts`. Só é usado
 * quando a resposta não traz `canMatch` (o servidor é a fonte; §7.1).
 */
export const MATCH_MIN_OVERLAP = 5;

/** De onde a lista foi vista — prop `door` de toda a telemetria (§8). */
export type PeopleDoor = "home" | "home_desktop" | "private_tab";

/**
 * A chave "Leitores parecidos" está LIGADA para este perfil?
 *
 * ⚠️ O campo é NEGATIVO (`peopleSuggestionsOptOut`): ausente = participa,
 * `=== true` = saiu (§5). A inversão ON↔campo é exatamente onde esse tipo de
 * chave erra, por isso é função e tem teste.
 */
export function isPeopleSuggestionsEnabled(
  profile: Pick<UserProfile, "peopleSuggestionsOptOut"> | null | undefined,
): boolean {
  return profile?.peopleSuggestionsOptOut !== true;
}

/** "Fantasia e Terror" — até 2 gêneros (os ids de `StepGenres` já são rótulos). */
export function joinGenres(genres: readonly string[]): string {
  const g = genres.filter((x) => typeof x === "string" && x.trim().length > 0).slice(0, 2);
  if (g.length === 0) return "";
  if (g.length === 1) return g[0];
  return `${g[0]} e ${g[1]}`;
}

/**
 * O bloco do meio do cartão (o "motivo"). Três formas, e o cartão nunca fica
 * sem a linha do motivo:
 *  - `book`: livro em destaque com capa ("Vocês leram" / "Vocês querem ler");
 *  - `genres`: só gênero ("Vocês curtem Fantasia e Terror"), sem capa;
 *  - `count`: estante em comum sem lido×lido nem quero×quero (Q12) —
 *    "Vocês têm N livros em comum", sem capa.
 */
export type PersonReason =
  | { kind: "book"; label: string; title: string; coverUrl: string }
  | { kind: "genres"; label: string; title: string }
  | { kind: "count"; text: string };

export function personReason(p: PeopleSuggestion): PersonReason {
  if (p.kind === "genre") {
    return { kind: "genres", label: "Vocês curtem", title: joinGenres(p.sharedGenres) };
  }
  if (p.topBook && p.topBook.title) {
    return {
      kind: "book",
      label: p.topBook.kind === "want" ? "Vocês querem ler" : "Vocês leram",
      title: p.topBook.title,
      coverUrl: p.topBook.coverUrl,
    };
  }
  const n = Math.max(0, p.commonCount);
  return { kind: "count", text: n === 1 ? "Vocês têm 1 livro em comum" : `Vocês têm ${n} livros em comum` };
}

/**
 * A linha pequena no acento do tema, abaixo do motivo.
 *
 * Com livro em destaque, "+N em comum" conta ALÉM do destaque (Q15): o
 * `commonCount` do servidor já inclui o `topBook`, então N = commonCount − 1.
 * Só-gênero: "sem livro em comum ainda" (mock 4e). Sem destaque, o próprio
 * motivo já diz o total — a linha some para não repetir o número.
 */
export function personCountLine(p: PeopleSuggestion): string | null {
  if (p.kind === "genre") return "sem livro em comum ainda";
  if (!p.topBook || !p.topBook.title) return null;
  const extra = p.commonCount - 1;
  return extra > 0 ? `+${extra} em comum` : null;
}

/**
 * A linha de contexto do cartão v2 (05/10, mock `leitores-parecidos-v2` E6):
 * SEMPRE presente, é ela que segura a altura igual entre cartões. Com livro em
 * destaque, o "+N" de sempre (ou "o único em comum"); sem destaque, quantos os
 * dois já LERAM (`readCommon`, que o servidor já manda); só gênero, a frase de
 * sempre.
 */
export function personContextLine(p: PeopleSuggestion): string {
  if (p.kind === "genre") return "sem livro em comum ainda";
  if (p.topBook && p.topBook.title) {
    const extra = p.commonCount - 1;
    return extra > 0 ? `+${extra} em comum` : "o único em comum";
  }
  const r = Math.max(0, p.readCommon);
  if (r === 0) return "nenhum lido pelos dois ainda";
  return r === 1 ? "1 lido pelos dois" : `${r} lidos pelos dois`;
}

/** "N livros na estante" — o título do motivo "número" no cartão v2. */
export function personCountTitle(p: Pick<PeopleSuggestion, "commonCount">): string {
  const n = Math.max(0, p.commonCount);
  return n === 1 ? "1 livro na estante" : `${n} livros na estante`;
}

/** O Match tem número para este par? `canMatch` do servidor manda (§7.1). */
export function opensMatch(p: Pick<PeopleSuggestion, "canMatch" | "readCommon">): boolean {
  if (typeof p.canMatch === "boolean") return p.canMatch;
  return p.readCommon >= MATCH_MIN_OVERLAP;
}

/**
 * O toque no cartão leva ao Match literário (≥ 5 lidos em comum, Q1/R7)?
 *
 * DESLIGADO por decisão do fundador em 05/10: "o Match ainda não foi polido o
 * suficiente para aparecer". O toque abre sempre o PERFIL. A regra do Match
 * (`opensMatch`, `canMatch` do servidor) fica pronta: religar é virar isto.
 */
export const PEOPLE_TAP_OPENS_MATCH = false;

/** Destino do toque no cartão: o perfil (o Match está desligado, ver acima). */
export function personTapTarget(
  p: Pick<PeopleSuggestion, "canMatch" | "readCommon" | "username">,
): { path: string; target: "match" | "profile" } {
  const handle = encodeURIComponent(p.username);
  return PEOPLE_TAP_OPENS_MATCH && opensMatch(p)
    ? { path: `/match/${handle}`, target: "match" }
    : { path: `/profile/${handle}`, target: "profile" };
}

/**
 * A porta "Chamar para um clube" (§7.2, F1). Só existe DEPOIS de seguir (E2).
 *
 *  - já em liga privada → nada (F2 é o convite, depois do C0);
 *  - PRO sem liga privada → `create` (abre o `CreateLeagueSheet`);
 *  - Free com compra disponível → `pro` (porta de conversão → `/pro`);
 *  - Free no app da loja sem compra → nada: o botão É um CTA de compra
 *    (mesma regra do `FriendsLeagueTab`, `purchaseAvailability.ts`).
 */
export type ClubDoor = "create" | "pro" | null;

export function clubDoorFor({
  following,
  isPro,
  inPrivateLeague,
  purchaseAvailable,
}: {
  following: boolean;
  isPro: boolean;
  inPrivateLeague: boolean;
  purchaseAvailable: boolean;
}): ClubDoor {
  if (!following) return null;
  if (inPrivateLeague) return null;
  if (isPro) return "create";
  return purchaseAvailable ? "pro" : null;
}

/** "Lu" de "Lu Andrade" — para "Criar um clube e chamar Lu". */
export function firstName(displayName: string): string {
  const first = displayName.trim().split(/\s+/)[0] ?? "";
  return first || displayName.trim();
}

/** Bucket de `common` do `people_suggestion_follow` (§8): sem número cru. */
export function commonBucket(commonCount: number): "0" | "3-4" | "5+" {
  if (commonCount <= 0) return "0";
  return commonCount >= 5 ? "5+" : "3-4";
}
